import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaCreditCard,
  FaSave,
  FaExclamationTriangle,
  FaLock,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import Notification from "../components/shared/Notification";
import { updateUserInfo } from "../api/auth";
import { profileData } from "../api/payment";
import { PuffLoader } from "react-spinners";
import PasswordChange from "../components/auth/PasswordChange";

const UserProfile = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("personal");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    identificacion: "",
    tipoId: "cedula",
    numeroCuenta: "",
    banco: "",
    tipoCuenta: "ahorros",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cargar datos del perfil
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const userProfileData = await profileData();

        if (userProfileData) {
          setFormData({
            nombre: userProfileData.first_name || "",
            apellido: userProfileData.last_name || "",
            identificacion: userProfileData.identification?.number || "",
            tipoId:
              userProfileData.identification?.type === "cedula"
                ? "cedula"
                : "nit",
            numeroCuenta: userProfileData.account?.number || "",
            banco: userProfileData.account?.bank || "",
            tipoCuenta: userProfileData.account?.type || "ahorros",
          });
        }
      } catch (error) {
        console.error("Error al cargar los datos del perfil:", error);
        setNotification({
          show: true,
          type: "error",
          message: "No se pudieron cargar tus datos de perfil",
        });

        setTimeout(() => {
          setNotification({ show: false, type: "", message: "" });
        }, 5000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  useEffect(() => {}, [formData]);

  const validateField = (name, value) => {
    let error = "";

    if (name === "nombre" || name === "apellido") {
      if (!value.trim()) {
        error = `El campo ${
          name === "nombre" ? "nombre" : "apellido"
        } es obligatorio`;
      }
    }

    if (name === "identificacion") {
      if (!value.trim()) {
        error = "El número de identificación es obligatorio";
      } else if (!/^\d+$/.test(value)) {
        error = "El número de identificación debe contener solo dígitos";
      }
    }

    if (name === "banco") {
      if (!value.trim()) {
        error = "Debes seleccionar un banco";
      }
    }

    if (name === "numeroCuenta") {
      if (!value.trim()) {
        error = "El número de cuenta es obligatorio";
      } else if (!/^\d+$/.test(value)) {
        error = "El número de cuenta debe contener solo dígitos";
      }
    }

    return error;
  };

  const handleChange = (e) => {
    // eslint-disable-next-line no-unused-vars
    const { name, value, type } = e.target;

    if (
      (name === "identificacion" || name === "numeroCuenta") &&
      !/^\d*$/.test(value)
    ) {
      return;
    }

    setFormData({
      ...formData,
      [name]: value,
    });

    if (!touched[name]) {
      setTouched({
        ...touched,
        [name]: true,
      });
    }

    const fieldError = validateField(name, value);
    setErrors({
      ...errors,
      [name]: fieldError,
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched({
      ...touched,
      [name]: true,
    });

    const fieldError = validateField(name, value);
    setErrors({
      ...errors,
      [name]: fieldError,
    });
  };

  const validateAll = () => {
    const newErrors = {};
    const allTouched = {};

    Object.keys(formData).forEach((field) => {
      allTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setTouched(allTouched);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateAll();

    if (!isValid) {
      const personalHasErrors =
        errors.nombre || errors.apellido || errors.identificacion;
      const paymentHasErrors = errors.banco || errors.numeroCuenta;

      setNotification({
        show: true,
        type: "error",
        message:
          "Por favor completa toda la información requerida en ambas secciones",
      });

      if (personalHasErrors) {
        setActiveTab("personal");
      } else if (paymentHasErrors) {
        setActiveTab("payment");
      }

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);

      return;
    }

    const dataForBackend = {
      first_name: formData.nombre,
      last_name: formData.apellido,
      identification: {
        number: formData.identificacion,
        type: formData.tipoId === "cedula" ? "cedula" : "NIT",
      },
      account: {
        number: formData.numeroCuenta,
        bank: formData.banco,
        type: formData.tipoCuenta === "ahorros" ? "ahorros" : "corriente",
      },
    };

    try {
      setIsSubmitting(true);
      const response = await updateUserInfo(dataForBackend);
      console.log("Respuesta del servidor:", response);

      setNotification({
        show: true,
        type: "success",
        message: "Todos tus datos han sido guardados correctamente",
      });

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);
    } catch (error) {
      console.error("Error al enviar datos: ", error);

      setNotification({
        show: true,
        type: "error",
        message: "Ha ocurrido un error al guardar tus datos",
      });

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotification = (notificationData) => {
    setNotification(notificationData);

    setTimeout(() => {
      setNotification({ show: false, type: "", message: "" });
    }, 5000);
  };

  const personalIsValid =
    formData.nombre.trim() !== "" &&
    formData.apellido.trim() !== "" &&
    formData.identificacion.trim() !== "" &&
    !errors.nombre &&
    !errors.apellido &&
    !errors.identificacion;

  const paymentIsValid =
    formData.numeroCuenta.trim() !== "" &&
    formData.banco.trim() !== "" &&
    !errors.numeroCuenta &&
    !errors.banco;

  if (isLoading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="user-profile__header">
        <div className="user-profile__avatar">
          <FaUser />
        </div>
        <div className="user-profile__info">
          <h1>
            {formData.nombre || "Usuario"} {formData.apellido || ""}
          </h1>
          <div className="user-profile__meta">
            <span className="user-badge">Usuario</span>
            <span className="user-profile__email">
              {user?.email || "email@ejemplo.com"}
            </span>
          </div>
        </div>
      </div>

      <Notification
        show={notification.show}
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ ...notification, show: false })}
      />

      <div className="user-profile__content">
        <div className="user-profile__tabs">
          <button
            className={`tab-button ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            <FaUser />
            <span>Datos Personales</span>
            {personalIsValid ? (
              <span className="tab-status tab-status--valid">✓</span>
            ) : (
              touched.nombre && (
                <span className="tab-status tab-status--invalid">
                  <FaExclamationTriangle />
                </span>
              )
            )}
          </button>
          <button
            className={`tab-button ${activeTab === "payment" ? "active" : ""}`}
            onClick={() => setActiveTab("payment")}
          >
            <FaCreditCard />
            <span>Método de Pago</span>
            {paymentIsValid ? (
              <span className="tab-status tab-status--valid">✓</span>
            ) : (
              touched.banco && (
                <span className="tab-status tab-status--invalid">
                  <FaExclamationTriangle />
                </span>
              )
            )}
          </button>
          <button
            className={`tab-button ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <FaLock />
            <span>Seguridad</span>
          </button>
        </div>

        <div className="user-profile__card">
          <form onSubmit={handleSubmit} className="form">
            {activeTab === "personal" ? (
              <>
                <div className="form__header">
                  <h2>Datos Personales</h2>
                  <p>Esta información es necesaria para procesar tus pagos</p>
                </div>

                <div className="form__row">
                  <div className="form__group">
                    <label htmlFor="nombre">Nombre</label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Ingresa tu nombre"
                      className={
                        touched.nombre && errors.nombre ? "input-error" : ""
                      }
                    />
                    {touched.nombre && errors.nombre && (
                      <div className="error-message">{errors.nombre}</div>
                    )}
                  </div>

                  <div className="form__group">
                    <label htmlFor="apellido">Apellido</label>
                    <input
                      type="text"
                      id="apellido"
                      name="apellido"
                      value={formData.apellido}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Ingresa tu apellido"
                      className={
                        touched.apellido && errors.apellido ? "input-error" : ""
                      }
                    />
                    {touched.apellido && errors.apellido && (
                      <div className="error-message">{errors.apellido}</div>
                    )}
                  </div>
                </div>

                <div className="form__row">
                  <div className="form__group">
                    <label htmlFor="tipoId">Tipo de identificación</label>
                    <select
                      id="tipoId"
                      name="tipoId"
                      value={formData.tipoId}
                      onChange={handleChange}
                    >
                      <option value="cedula">Cédula</option>
                      <option value="nit">NIT</option>
                    </select>
                  </div>

                  <div className="form__group">
                    <label htmlFor="identificacion">
                      Número de identificación
                    </label>
                    <input
                      type="text"
                      id="identificacion"
                      name="identificacion"
                      value={formData.identificacion}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Ingresa tu número de identificación"
                      data-type="numeric"
                      className={
                        touched.identificacion && errors.identificacion
                          ? "input-error"
                          : ""
                      }
                    />
                    {touched.identificacion && errors.identificacion && (
                      <div className="error-message">
                        {errors.identificacion}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form__actions">
                  <button
                    type="submit"
                    className="btn btn-primary btn-save-all"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Guardando..." : "Guardar Todos los Datos"}
                    {!isSubmitting && <FaSave className="btn-icon" />}
                  </button>
                </div>
              </>
            ) : activeTab === "payment" ? (
              <>
                <div className="form__header">
                  <h2>Método de Pago</h2>
                  <p>Completa esta información para recibir tus pagos</p>
                </div>

                <div className="form__row">
                  <div className="form__group">
                    <label htmlFor="banco">Banco</label>
                    <select
                      id="banco"
                      name="banco"
                      value={formData.banco}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        touched.banco && errors.banco ? "input-error" : ""
                      }
                    >
                      <option value="">Selecciona un banco</option>
                      <option value="bancolombia">Bancolombia</option>
                      <option value="davivienda">Davivienda</option>
                      <option value="bbva">BBVA</option>
                      <option value="bancodebogota">Banco de Bogotá</option>
                      <option value="nequi">Nequi</option>
                      <option value="daviplata">Daviplata</option>
                    </select>
                    {touched.banco && errors.banco && (
                      <div className="error-message">{errors.banco}</div>
                    )}
                  </div>

                  <div className="form__group">
                    <label htmlFor="tipoCuenta">Tipo de cuenta</label>
                    <select
                      id="tipoCuenta"
                      name="tipoCuenta"
                      value={formData.tipoCuenta}
                      onChange={handleChange}
                    >
                      <option value="ahorros">Ahorros</option>
                      <option value="corriente">Corriente</option>
                    </select>
                  </div>
                </div>

                <div className="form__group">
                  <label htmlFor="numeroCuenta">Número de cuenta</label>
                  <input
                    type="text"
                    id="numeroCuenta"
                    name="numeroCuenta"
                    value={formData.numeroCuenta}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Ingresa tu número de cuenta"
                    data-type="numeric"
                    className={
                      touched.numeroCuenta && errors.numeroCuenta
                        ? "input-error"
                        : ""
                    }
                  />
                  {touched.numeroCuenta && errors.numeroCuenta && (
                    <div className="error-message">{errors.numeroCuenta}</div>
                  )}
                </div>

                <div className="form__actions">
                  <button
                    type="submit"
                    className="btn btn-primary btn-save-all"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Guardando..." : "Guardar Todos los Datos"}
                    {!isSubmitting && <FaSave className="btn-icon" />}
                  </button>
                </div>
              </>
            ) : (
              <PasswordChange onNotification={handleNotification} />
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
